import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { CircularProgress } from '@mui/material'
import { formatNumber } from '../../../helpers/formatNumber'
import Decimal from '../../old-components/markets/Decimal'
import ShimmerLine from '../../../../components/shimmer-components/ShimmerLine'
import { ReactComponent as UpWhiteArrow } from '../../../../assets/icons/up-white-arrow.svg'
import { useDispatch, useSelector } from 'react-redux'

// ** Styles Imports
import Styles from './orderbookBody.module.css'
import rowStyles from './OrderbookRow.module.css'

// ** Helper Imports
import {
  groupByTicketSize,
  addTotalSums,
  addDepths,
  sortAsks,
  sortBids,
  applyUpdatesToRawData,
  getMaxTotalSum,
  sortAndFilterOrders
} from '../../redux/helpers'
import Button from '../../../../components/Button/Button'
import { setLimitPrice } from '../../redux/slices/limitPriceSlice'

const MAX_ORDER_BOOK_ENTRIES = 10

function OrderbookBody() {
  const [orderBook, setOrderBook] = useState({
    asks: [],
    bids: [],
    currentPrice: 0
  })
  const dispatch = useDispatch()

  const grouping = useSelector(state => state.orderBook.groupingSize)
  const selectedMarket = useSelector(state => state.selectedMarket.value)
  const lastTrade = useSelector(state => state.trades.last)
  const theme = useSelector(state => state.theme.value)
  const wsRef = useRef(null)
  const isInitialMount = useRef(true)
  const currentMarketId = useRef(selectedMarket?.id)
  const [wsConnected, setWsConnected] = useState(false)

  // Store raw order data separately from grouped data
  const [rawOrderBook, setRawOrderBook] = useState({
    asks: [],
    bids: []
  })

  const handleRowClick = price => {
    // console.log(price)
    dispatch(setLimitPrice(price))
  }

  const roundToNearest = (value, interval) => {
    return Math.floor(value / interval) * interval
  }
  // Group orders by price level
  const groupOrders = useCallback((orders, groupSize, type) => {
    const groupedOrders = new Map()
    orders.forEach(order => {
      // For asks, round up to next group if above last trade price
      let groupPrice
      if (type === 'asks') {
        // Round up to next group using Math.ceil instead of Math.floor
        groupPrice = Math.ceil(order.price / groupSize) * groupSize
      } else {
        // For bids, continue using Math.floor
        groupPrice = Math.floor(order.price / groupSize) * groupSize
      }

      if (!groupedOrders.has(groupPrice)) {
        groupedOrders.set(groupPrice, {
          price: groupPrice,
          amount: 0,
          total: 0
        })
      }
      const group = groupedOrders.get(groupPrice)
      group.amount += order.amount
      group.total += order.amount * order.price
    })

    // Different sorting for asks and bids
    const groupedArray = Array.from(groupedOrders.values())
    return type === 'asks'
      ? groupedArray.sort((a, b) => b.price - a.price) // Asks sorted high to low
      : groupedArray.sort((a, b) => b.price - a.price) // Bids remain sorted high to low
  }, [])

  // Process and group orders
  const processOrders = useCallback(
    rawBook => {
      const processedBook = {
        asks: [...rawBook.asks],
        bids: [...rawBook.bids]
      }

      // Filter asks and bids based on lastTrade price
      if (lastTrade && lastTrade.price) {
        processedBook.asks = processedBook.asks.filter(ask => ask.price > lastTrade.price)
        processedBook.bids = processedBook.bids.filter(bid => bid.price < lastTrade.price)
      }

      // Group the orders
      processedBook.asks = groupOrders(processedBook.asks, grouping, 'asks')
      processedBook.bids = groupOrders(processedBook.bids, grouping, 'bids')
      // console.log(processedBook.asks)
      // console.log(processedBook.bids)

      // Sort and filter
      const sortedAndFilteredAsks = sortAndFilterOrders(processedBook.asks, lastTrade, 'asks')
      const sortedAndFilteredBids = sortAndFilterOrders(processedBook.bids, lastTrade, 'bids')

      // Calculate depths
      const maxTotalAsks = getMaxTotalSum(sortedAndFilteredAsks)
      const maxTotalBids = getMaxTotalSum(sortedAndFilteredBids)
      processedBook.asks = addDepths(sortedAndFilteredAsks, maxTotalAsks)
      processedBook.bids = addDepths(sortedAndFilteredBids, maxTotalBids)

      // Limit the number of orders
      if (processedBook.asks.length > 500) {
        processedBook.asks = processedBook.asks.slice(0, 500)
      }
      if (processedBook.bids.length > 500) {
        processedBook.bids = processedBook.bids.slice(0, 500)
      }

      return processedBook
    },
    [groupOrders, lastTrade, grouping]
  )

  const handleWebSocketMessage = useCallback(event => {
    const data = JSON.parse(event.data)

    setRawOrderBook(prevRawBook => {
      const newRawBook = {
        asks: [...prevRawBook.asks],
        bids: [...prevRawBook.bids]
      }

      // Apply snapshot data
      if (data[`${currentMarketId.current}.ob-snap`]) {
        const snapshotAsks = data[`${currentMarketId.current}.ob-snap`]?.asks || []
        const snapshotBids = data[`${currentMarketId.current}.ob-snap`]?.bids || []

        newRawBook.asks = snapshotAsks.map(([price, amount]) => ({ price, amount, total: price * amount }))
        newRawBook.bids = snapshotBids.map(([price, amount]) => ({ price, amount, total: price * amount }))
      }

      // Apply incremental updates
      data[`${currentMarketId.current}.ob-inc`]?.asks?.forEach(([price, amount]) => {
        if (amount === 0) {
          newRawBook.asks = newRawBook.asks.filter(ask => ask.price !== price)
        } else {
          const index = newRawBook.asks.findIndex(ask => ask.price === price)
          const newOrder = { price, amount, total: price * amount }
          if (index === -1) {
            newRawBook.asks.push(newOrder)
          } else {
            newRawBook.asks[index] = newOrder
          }
        }
      })

      data[`${currentMarketId.current}.ob-inc`]?.bids?.forEach(([price, amount]) => {
        if (amount === 0) {
          newRawBook.bids = newRawBook.bids.filter(bid => bid.price !== price)
        } else {
          const index = newRawBook.bids.findIndex(bid => bid.price === price)
          const newOrder = { price, amount, total: price * amount }
          if (index === -1) {
            newRawBook.bids.push(newOrder)
          } else {
            newRawBook.bids[index] = newOrder
          }
        }
      })

      return newRawBook
    })
  }, [])

  // Process raw orders whenever rawOrderBook or grouping changes
  useEffect(() => {
    const processedBook = processOrders(rawOrderBook)
    setOrderBook(processedBook)
  }, [rawOrderBook, grouping, processOrders])

  // Rest of the component remains the same...
  // (setupWebSocket, switchMarket, useEffects, rendering logic)

  const setupWebSocket = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
      wsRef.current = new WebSocket(
        window.location.host.includes('localhost')
          ? `ws://${window.location.host}/api/v2/ws/public`
          : `wss://${window.location.host}/api/v2/ws/public`
      )

      wsRef.current.onopen = () => {
        setWsConnected(true)
        const subscribeMsg = {
          event: 'subscribe',
          streams: [`${currentMarketId.current}.ob-snap`, `${currentMarketId.current}.ob-inc`]
        }
        wsRef.current.send(JSON.stringify(subscribeMsg))
      }

      wsRef.current.onclose = () => {
        setWsConnected(false)
      }

      wsRef.current.onerror = error => {
        // console.error("WebSocket error:", error)
        setWsConnected(false)
      }

      wsRef.current.onmessage = handleWebSocketMessage
    }
  }, [handleWebSocketMessage])

  const switchMarket = useCallback(
    async newMarketId => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setupWebSocket()
        return
      }

      const unsubscribeMsg = {
        event: 'unsubscribe',
        streams: [`${currentMarketId.current}.ob-snap`, `${currentMarketId.current}.ob-inc`]
      }
      wsRef.current.send(JSON.stringify(unsubscribeMsg))

      setRawOrderBook({
        asks: [],
        bids: []
      })

      currentMarketId.current = newMarketId

      const subscribeMsg = {
        event: 'subscribe',
        streams: [`${newMarketId}.ob-snap`, `${newMarketId}.ob-inc`]
      }
      wsRef.current.send(JSON.stringify(subscribeMsg))
    },
    [setupWebSocket]
  )

  // useEffect(() => {
  //   console.log(lastTrade)
  // }, [lastTrade])

  useEffect(() => {
    // console.log(selectedMarket)

    if (isInitialMount.current) {
      setupWebSocket()
      isInitialMount.current = false
    }

    return () => {
      if (wsRef.current) {
        const unsubscribeMsg = {
          event: 'unsubscribe',
          streams: [`${currentMarketId.current}.ob-snap`, `${currentMarketId.current}.ob-inc`]
        }

        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify(unsubscribeMsg))
        }

        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isInitialMount.current && selectedMarket.id !== currentMarketId.current && wsConnected) {
      switchMarket(selectedMarket.id)
    }
  }, [selectedMarket.id, wsConnected, switchMarket])

  const maxVolume = useMemo(() => {
    const askMax = Math.max(...orderBook.asks.map(ask => ask.total))
    const bidMax = Math.max(...orderBook.bids.map(bid => bid.total))
    return Math.max(askMax, bidMax)
  }, [orderBook])

  const renderOrderRow = (order, type) => {
    const percentage = (order.total / maxVolume) * 100
    const bgColor = type === 'ask' ? 'bg-red-900/20' : 'bg-green-900/20'

    return (
      <div className='relative grid grid-cols-3 text-sm h-8 items-center'>
        <div className={`absolute right-0 h-full ${bgColor} z-0`} style={{ width: `${percentage}%` }} />
        <div className={`z-10 ${type === 'ask' ? 'text-red-500' : 'text-green-500'}`}>{formatNumber(order.price)}</div>
        <div className='text-right z-10'>{formatNumber(order.amount)}</div>
        <div className='text-right z-10'>{formatNumber(order.total)}</div>
      </div>
    )
  }

  const formatPrice = arg => {
    return arg.toLocaleString('en', {
      useGrouping: true,
      minimumFractionDigits: 2
    })
  }

  const buildPriceLevels = (levels, orderType) => {
    let sortedLevelsByPrice = levels
    if (orderType === 'asks') {
      // For asks, we reverse the array to show highest price at top
      sortedLevelsByPrice = [...levels].reverse()
    }

    return sortedLevelsByPrice.map((level, index) => {
      const depth = parseFloat(level['depth'])
      const size = formatNumber(parseFloat(level['amount']), 5)
      const price = formatPrice(level['price'])
      const total = formatNumber(level['total'])
      const isAsk = orderType === 'asks'

      return (
        <div
          key={`row-${index}`}
          onClick={e => handleRowClick(price)}
          className={`${rowStyles.rowContainer} ${isAsk ? rowStyles['sell-row'] : rowStyles['buy-row']} ${
            theme === 'light' ? rowStyles.light : ''
          }`}
          style={{ '--depth': `${depth}%` }}
        >
          <div className={rowStyles.price}>{price}</div>
          <div className={rowStyles.amount}>{size}</div>
          <div className={rowStyles.total}>{total}</div>
          <span className={rowStyles.rowBackground}></span>
        </div>
      )
    })
  }

  return (
    <div className={`${Styles.card} ${theme === 'light' ? Styles.light : ''}`}>
      {orderBook.asks && orderBook.bids ? (
        <div>
          <div className={Styles.sortingContainer}>
            <span>Price ({selectedMarket.quote_unit?.toUpperCase()})</span>
            <span>Amount ({selectedMarket.base_unit?.toUpperCase()})</span>
            <span>Total ({selectedMarket.quote_unit?.toUpperCase()})</span>
          </div>

          <div className={Styles.bidsContainer}>
            {wsConnected ? (
              buildPriceLevels(orderBook.asks.slice(0, MAX_ORDER_BOOK_ENTRIES), 'asks')
            ) : (
              <CircularProgress
                sx={{
                  margin: '35% 0 0 45%'
                  // margin: "90px 0 0 0"
                }}
              />
            )}
          </div>

          {lastTrade && lastTrade?.price ? (
            <div
              className={Styles.lastTrade}
              style={{
                color: lastTrade.taker_type === 'buy' ? 'var(--negative-color)' : 'var(--positive-color)'
              }}
            >
              {formatNumber(lastTrade.price)} {selectedMarket.quote_unit?.toUpperCase()}
              <UpWhiteArrow
                width={17}
                style={{
                  transform:
                    lastTrade.taker_type === 'buy'
                      ? 'rotateY(180deg) rotateZ(90deg) rotateX(180deg)'
                      : 'rotateX(180deg) rotateZ(90deg)'
                }}
              />
            </div>
          ) : (
            <ShimmerLine style={{ margin: '10px' }} width='100%' height='45px' />
          )}

          <div className={Styles.asksContainer}>
            {wsConnected ? (
              buildPriceLevels(orderBook.bids.slice(0, MAX_ORDER_BOOK_ENTRIES), 'bids')
            ) : (
              <CircularProgress
                sx={{
                  margin: '35% 0 0 45%'
                  // margin: "90px 0 0 0"
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div className={Styles.loadingContainer}>
          <CircularProgress />
        </div>
      )}
    </div>
  )
}

export default OrderbookBody
// import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
// import { CircularProgress } from '@mui/material'
// import { formatNumber } from '../../../helpers/formatNumber'
// import Decimal from '../../old-components/markets/Decimal'
// import ShimmerLine from '../../../../components/shimmer-components/ShimmerLine'
// import { ReactComponent as UpWhiteArrow } from '../../../../assets/icons/up-white-arrow.svg'
// import { useDispatch, useSelector } from 'react-redux'

// // ** Styles Imports
// import Styles from './orderbookBody.module.css'
// import rowStyles from './OrderbookRow.module.css'

// // ** Helper Imports
// import { groupByTicketSize, addTotalSums, addDepths, sortAsks, sortBids, applyUpdatesToRawData, getMaxTotalSum, sortAndFilterOrders } from '../../redux/helpers'
// import Button from '../../../../components/Button/Button'
// import { setLimitPrice } from '../../redux/slices/limitPriceSlice'

// const MAX_ORDER_BOOK_ENTRIES = 10

// // Market symbol mapping for Kraken
// const MARKET_SYMBOL_MAP = {
//   'btcusd': 'BTC/USD',
//   'ethusd': 'ETH/USD',
//   'xrpusd': 'XRP/USD',
//   // Add more mappings as needed
// }

// function OrderbookBody() {
//   const [orderBook, setOrderBook] = useState({
//     asks: [],
//     bids: [],
//     currentPrice: 0,
//   })
//   const dispatch = useDispatch()

//   const grouping = useSelector(state => state.orderBook.groupingSize)
//   const selectedMarket = useSelector(state => state.selectedMarket.value)
//   const lastTrade = useSelector(state => state.trades.last)
//   const wsRef = useRef(null)
//   const isInitialMount = useRef(true)
//   const currentMarketSymbol = useRef(MARKET_SYMBOL_MAP[selectedMarket.id] || 'BTC/USD')
//   const [wsConnected, setWsConnected] = useState(false)

//   // Store raw order data separately from grouped data
//   const [rawOrderBook, setRawOrderBook] = useState({
//     asks: [],
//     bids: []
//   })

//   const handleRowClick = price => {
//     dispatch(setLimitPrice(price))
//   }

//   // Group orders by price level (similar to previous implementation)
//   const groupOrders = useCallback((orders, groupSize, type) => {
//     const groupedOrders = new Map()
//     orders.forEach(order => {
//       let groupPrice
//       if (type === 'asks') {
//         groupPrice = Math.ceil(order.price / groupSize) * groupSize
//       } else {
//         groupPrice = Math.floor(order.price / groupSize) * groupSize
//       }

//       if (!groupedOrders.has(groupPrice)) {
//         groupedOrders.set(groupPrice, {
//           price: groupPrice,
//           amount: 0,
//           total: 0,
//         })
//       }
//       const group = groupedOrders.get(groupPrice)
//       group.amount += order.amount
//       group.total += order.amount * order.price
//     })

//     const groupedArray = Array.from(groupedOrders.values())
//     return type === 'asks'
//       ? groupedArray.sort((a, b) => b.price - a.price)
//       : groupedArray.sort((a, b) => b.price - a.price)
//   }, [])

//   // Process and group orders (similar to previous implementation)
//   const processOrders = useCallback((rawBook) => {
//     const processedBook = {
//       asks: [...rawBook.asks],
//       bids: [...rawBook.bids]
//     }

//     if (lastTrade && lastTrade.price) {
//       processedBook.asks = processedBook.asks.filter((ask) => ask.price > lastTrade.price)
//       processedBook.bids = processedBook.bids.filter((bid) => bid.price < lastTrade.price)
//     }

//     processedBook.asks = groupOrders(processedBook.asks, grouping, 'asks')
//     processedBook.bids = groupOrders(processedBook.bids, grouping, 'bids')

//     const sortedAndFilteredAsks = sortAndFilterOrders(processedBook.asks, lastTrade, 'asks')
//     const sortedAndFilteredBids = sortAndFilterOrders(processedBook.bids, lastTrade, 'bids')

//     const maxTotalAsks = getMaxTotalSum(sortedAndFilteredAsks)
//     const maxTotalBids = getMaxTotalSum(sortedAndFilteredBids)
//     processedBook.asks = addDepths(sortedAndFilteredAsks, maxTotalAsks)
//     processedBook.bids = addDepths(sortedAndFilteredBids, maxTotalBids)

//     if (processedBook.asks.length > 500) {
//       processedBook.asks = processedBook.asks.slice(0, 500)
//     }
//     if (processedBook.bids.length > 500) {
//       processedBook.bids = processedBook.bids.slice(0, 500)
//     }

//     return processedBook
//   }, [groupOrders, lastTrade, grouping])

//   // Handle WebSocket messages for Kraken
//   const handleWebSocketMessage = useCallback((event) => {
//     const data = JSON.parse(event.data)

//     // Check if this is a book update message
//     if (data.channel === 'book' && data.type === 'update') {
//       const bookData = data.data[0]

//       // Only process if the symbol matches
//       if (bookData.symbol === currentMarketSymbol.current) {
//         setRawOrderBook((prevRawBook) => {
//           const newRawBook = {
//             asks: bookData.asks.map(ask => ({
//               price: ask.price,
//               amount: ask.qty,
//               total: ask.price * ask.qty
//             })),
//             bids: bookData.bids.map(bid => ({
//               price: bid.price,
//               amount: bid.qty,
//               total: bid.price * bid.qty
//             }))
//           }

//           return newRawBook
//         })
//       }
//     }
//   }, [])

//   // Process raw orders whenever rawOrderBook or grouping changes
//   useEffect(() => {
//     const processedBook = processOrders(rawOrderBook)
//     setOrderBook(processedBook)
//   }, [rawOrderBook, grouping, processOrders])

//   // Setup WebSocket connection to Kraken
//   const setupWebSocket = useCallback(() => {
//     if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
//       wsRef.current = new WebSocket('wss://ws-frontend.kraken.com/v2')

//       wsRef.current.onopen = () => {
//         setWsConnected(true)
//         const subscribeMsg = {
//           method: "subscribe",
//           params: {
//             channel: "book",
//             symbol: [currentMarketSymbol.current],
//             depth: 100,
//             grouping: 1
//           }
//         }
//         wsRef.current.send(JSON.stringify(subscribeMsg))
//       }

//       wsRef.current.onclose = () => {
//         setWsConnected(false)
//       }

//       wsRef.current.onerror = (error) => {
//         console.error("WebSocket error:", error)
//         setWsConnected(false)
//       }

//       wsRef.current.onmessage = handleWebSocketMessage
//     }
//   }, [handleWebSocketMessage])

//   // Switch market logic for Kraken
//   const switchMarket = useCallback(async (newMarketId) => {
//     if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
//       setupWebSocket()
//       return
//     }

//     const krakenSymbol = MARKET_SYMBOL_MAP[newMarketId] || 'BTC/USD'

//     // Unsubscribe from current market
//     const unsubscribeMsg = {
//       method: "unsubscribe",
//       params: {
//         channel: "book",
//         symbol: [currentMarketSymbol.current],
//         depth: 100,
//         grouping: 1000
//       }
//     }
//     wsRef.current.send(JSON.stringify(unsubscribeMsg))

//     // Reset order book
//     setRawOrderBook({
//       asks: [],
//       bids: [],
//     })

//     // Update current market symbol
//     currentMarketSymbol.current = krakenSymbol

//     // Subscribe to new market
//     const subscribeMsg = {
//       method: "subscribe",
//       params: {
//         channel: "book",
//         symbol: [krakenSymbol],
//         depth: 100,
//         grouping: 1000
//       }
//     }
//     wsRef.current.send(JSON.stringify(subscribeMsg))
//   }, [setupWebSocket])

//   // Initial WebSocket setup and cleanup
//   useEffect(() => {
//     if (isInitialMount.current) {
//       setupWebSocket()
//       isInitialMount.current = false
//     }

//     return () => {
//       if (wsRef.current) {
//         const unsubscribeMsg = {
//           method: "unsubscribe",
//           params: {
//             channel: "book",
//             symbol: [currentMarketSymbol.current],
//             depth: 100,
//             grouping: 1000
//           }
//         }

//         if (wsRef.current.readyState === WebSocket.OPEN) {
//           wsRef.current.send(JSON.stringify(unsubscribeMsg))
//         }

//         wsRef.current.close()
//         wsRef.current = null
//       }
//     }
//   }, [])

//   // Market switching effect
//   useEffect(() => {
//     if (!isInitialMount.current &&
//       selectedMarket.id !== Object.keys(MARKET_SYMBOL_MAP).find(
//         key => MARKET_SYMBOL_MAP[key] === currentMarketSymbol.current
//       ) &&
//       wsConnected) {
//       switchMarket(selectedMarket.id)
//     }
//   }, [selectedMarket.id, wsConnected, switchMarket])

//   // Rest of the component remains the same as in the previous implementation
//   const maxVolume = useMemo(() => {
//     const askMax = Math.max(...orderBook.asks.map(ask => ask.total))
//     const bidMax = Math.max(...orderBook.bids.map(bid => bid.total))
//     return Math.max(askMax, bidMax)
//   }, [orderBook])

//   const renderOrderRow = (order, type) => {
//     const percentage = (order.total / maxVolume) * 100
//     const bgColor = type === 'ask' ? 'bg-red-900/20' : 'bg-green-900/20'

//     return (
//       <div className="relative grid grid-cols-3 text-sm h-8 items-center">
//         <div
//           className={`absolute right-0 h-full ${bgColor} z-0`}
//           style={{ width: `${percentage}%` }}
//         />
//         <div className={`z-10 ${type === 'ask' ? 'text-red-500' : 'text-green-500'}`}>
//           {formatNumber(order.price)}
//         </div>
//         <div className="text-right z-10">{formatNumber(order.amount)}</div>
//         <div className="text-right z-10">{formatNumber(order.total)}</div>
//       </div>
//     )
//   }

//   const formatPrice = (arg) => {
//     return arg.toLocaleString('en', {
//       useGrouping: true,
//       minimumFractionDigits: 2,
//     })
//   }

//   const buildPriceLevels = (levels, orderType) => {
//     let sortedLevelsByPrice = levels
//     if (orderType === 'asks') {
//       sortedLevelsByPrice = [...levels].reverse()
//     }

//     const rowBackgroundColor = orderType === 'bids' ? 'rgb(3, 166, 109, 0.2)' : 'rgb(220, 38, 38, 0.2)'
//     const rowColor = orderType === 'bids' ? 'var(--positive-color)' : 'var(--negative-color)'

//     return sortedLevelsByPrice.map((level, index) => {
//       const depth = parseFloat(level['depth'])
//       const size = formatNumber(parseFloat(level['amount']), 5)
//       const price = formatPrice(level['price'])
//       const total = formatNumber(level['total'])

//       return (
//         <div key={`row-${index}`} onClick={e => handleRowClick(price)} className={rowStyles.rowContainer}>
//           <div style={{ color: rowColor }}>{price}</div>
//           <div>{size}</div>
//           <span
//             className={rowStyles.rowBackground}
//             style={{
//               width: `${depth}%`,
//               backgroundColor: rowBackgroundColor,
//             }}
//           ></span>
//           <div>{total}</div>
//         </div>
//       )
//     })
//   }

//   return (
//     <div className={Styles.card}>
//       {orderBook.asks && orderBook.bids ? (
//         <div>
//           <div className={Styles.sortingContainer}>
//             <span>Price ({selectedMarket.quote_unit?.toUpperCase()})</span>
//             <span>Amount ({selectedMarket.base_unit?.toUpperCase()})</span>
//             <span>Total ({selectedMarket.quote_unit?.toUpperCase()})</span>
//           </div>

//           <div className={Styles.bidsContainer}>
//             {orderBook.asks.length
//               ? buildPriceLevels(orderBook.asks.slice(0, MAX_ORDER_BOOK_ENTRIES), 'asks')
//               : new Array(9).fill(0).map((_, i) => <ShimmerLine key={i} width="100%" height="24px" />)}
//           </div>

//           {lastTrade && lastTrade?.price ? (
//             <div
//               className={Styles.lastTrade}
//               style={{
//                 color: lastTrade.taker_type === 'buy' ? 'var(--negative-color)' : 'var(--positive-color)',
//               }}
//             >
//               {formatNumber(lastTrade.price)}{' '}
//               {selectedMarket.quote_unit?.toUpperCase()}
//               <UpWhiteArrow
//                 width={17}
//                 style={{
//                   transform:
//                     lastTrade.taker_type === 'buy'
//                       ? 'rotateY(180deg) rotateZ(90deg) rotateX(180deg)'
//                       : 'rotateX(180deg) rotateZ(90deg)',
//                 }}
//               />
//             </div>
//           ) : (
//             <ShimmerLine style={{ margin: '10px' }} width="100%" height="45px" />
//           )}

//           <div className={Styles.asksContainer}>
//             {orderBook.bids.length
//               ? buildPriceLevels(orderBook.bids.slice(0, MAX_ORDER_BOOK_ENTRIES), 'bids')
//               : new Array(9).fill(0).map((_, i) => <ShimmerLine key={i} width="100%" height="24px" />)}
//           </div>
//         </div>
//       ) : (
//         <div className={Styles.loadingContainer}>
//           <CircularProgress />
//         </div>
//       )
//       }
//     </div >
//   )
// }

// export default OrderbookBody
